/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, signal,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ArchiveService } from '../services/archive.service';
import { ArticleService } from '../services/article.service';
import { MarkdownService } from '../services/markdown.service';

// /articles/:articleId in the 2026 design: a reading column with one state alert (draft, hidden, archived,
// rejected, pending review — as ut-single-article), the shared action bar, "Articles You May Like" recommendations and
// the 2026 comment thread, which keeps the ?comments / ?sort deep links.
@Component({
  selector: 'ut-article-page',
  templateUrl: './article-page.component.html',
  styleUrls: ['./article-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ArticlePageComponent implements OnInit {
  // The `singlearticle` state's article.
  @Input({ required: true }) article: any;
  // The root state's `user`: null when logged out.
  @Input() user: any;

  body: SafeHtml = '';
  // The thread's live total. Set from `(totalChange)` so the action bar's count follows comments posted or deleted
  // after the page opened (this view is OnPush and the thread only updates its own view).
  threadCount = signal<number | null>(null);

  constructor(
    readonly articleService: ArticleService,
    private archiveService: ArchiveService,
    private markdownService: MarkdownService,
    private sanitizer: DomSanitizer,
    @Inject('MetaService') private MetaService: any,
    @Inject('AlertService') private AlertService: any,
    @Inject('$analytics') private $analytics: any,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    if (this.article.canonical) this.MetaService.set('canonical', this.article.canonical);
    const html = this.article.content ? this.markdownService.process(this.article.content) : '';
    this.body = this.sanitizer.bypassSecurityTrustHtml(html);
  }

  // One alert for the article's state, with the same messages as ut-single-article.
  get alert(): { kind: 'warning' | 'error'; text: string } | null {
    switch (this.article.state) {
      case 'draft': return { kind: 'warning', text: 'This article is not published yet.' };
      case 'hidden': return { kind: 'error', text: 'This article is hidden.' };
      case 'archived': return { kind: 'warning', text: 'This article has been archived. Its contents may be outdated. Please check the articles page for up-to-date content.' };
      case 'rejected': return { kind: 'warning', text: `This article has been rejected for the following reason: ${this.article.reject_reason || 'Unknown'}. Please update the article and re-submit.` };
      case 'review': return { kind: 'warning', text: 'Article is currently pending review.' };
      default: return null;
    }
  }

  // "Proven Tester · Brazil", skipping the parts a user doesn't have; the template adds the relative date.
  get authorMeta(): string {
    return [this.article.user?.user_title, this.article.user?.country?.name].filter(Boolean).join(' · ');
  }

  get canManage(): boolean {
    return !!(this.article.current_user_permissions?.update || this.article.current_user_permissions?.archive);
  }

  get commentCount(): number {
    return this.threadCount() ?? (this.article.comments?.count || 0);
  }

  // Same event as ut-recommendations.
  trackRecommendation(item: any): void {
    this.$analytics.eventTrack('click', { category: 'Article Recommendation', label: item.slug });
  }

  refresh(): void {
    this.cdr.markForCheck();
  }

  toggleArchive(): void {
    const archived = this.article.state === 'archived';
    const request = archived ? this.archiveService.unarchive(this.article) : this.archiveService.archive(this.article);
    request.then((res) => {
      Object.assign(this.article, res.data);
      this.AlertService.add(archived ? 'You successfully unarchived this article' : 'You successfully archived this article');
      this.cdr.markForCheck();
    }, () => this.AlertService.add('Request failed', 'danger'));
  }
}
